import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  OutlinedInput,
  SelectChangeEvent,
  CircularProgress,
  Checkbox,
  ListItemText,
} from '@mui/material';
import type { Subject } from '@timetable/types';

interface SubjectAssignmentProps {
  subjects: Subject[];
  selectedSubjects: string[];
  onSelectionChange: (subjectIds: string[]) => void;
  isLoading?: boolean;
}

export function SubjectAssignment({
  subjects,
  selectedSubjects,
  onSelectionChange,
  isLoading = false,
}: SubjectAssignmentProps) {
  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    onSelectionChange(typeof value === 'string' ? value.split(',') : value);
  };

  return (
    <FormControl fullWidth>
      <InputLabel>Subjects</InputLabel>
      <Select
        multiple
        value={selectedSubjects}
        onChange={handleChange}
        input={<OutlinedInput label="Subjects" />}
        disabled={isLoading}
        MenuProps={{ autoFocus: false }}
        renderValue={(selected) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selected.map((subjectId) => {
              const subject = subjects.find((s) => s.id === subjectId);
              return subject ? (
                <Chip
                  key={subjectId}
                  label={subject.subject_name}
                  size="small"
                  sx={{
                    backgroundColor: subject.color_code || undefined,
                    color: subject.color_code ? '#fff' : undefined,
                  }}
                />
              ) : null;
            })}
          </Box>
        )}
      >
        {isLoading ? (
          <MenuItem disabled>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              Loading subjects...
            </Box>
          </MenuItem>
        ) : subjects.length === 0 ? (
          <MenuItem disabled>No subjects available</MenuItem>
        ) : (
          subjects.map((subject) => (
            <MenuItem key={subject.id} value={subject.id}>
              <Checkbox checked={selectedSubjects.includes(subject.id)} />
              {subject.color_code && (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: subject.color_code,
                    mr: 1,
                  }}
                />
              )}
              <ListItemText primary={subject.subject_name} />
            </MenuItem>
          ))
        )}
      </Select>
    </FormControl>
  );
}
